import { DEFAULT_SELECT_PAGE_SIZE, FETCH_QUERIES_NAME, SYSTEM_ACCOUNT_DISPLAY_NAME } from '@common/constants';
import {
  FormSelect,
  IOption,
  requiredRule,
  FormFieldMethods,
  Grid,
  ComponentType,
  Icon,
  IconName,
  FormText,
  FormCheckbox,
} from '@gcl/react-core';
import * as React from 'react';
import {
  useFetchSrpOptionsWithSearch,
  useFetchTwcOptionsWithSearch,
  useFetchTwcmOptionsWithSearch,
} from '@userProfile/userProfile.queries';
import { FormSelectAsyncPaginate } from '@common/FormSelectAsyncPaginate';
import { LabelWithMessage } from '@common/LabelWithMessage';
import { IProjectCompanyOption } from '@company/projectCompany/projectCompany.models';
import { IRiskCategory } from '@riskCategory/riskCategory.models';
import { IProjectTeamOption } from '@projectTeam/projectTeam.models';
import { useFetchTeamsOptionsByCompanyId } from '@projectTeam/projectTeam.queries';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { ERiskCategory } from '@riskCategory/riskCategory.constants';
import { EOrganizationTypeCode } from '@company/organizationType/organizationType.constants';
import { IProjectUserProfileWithTeamOption } from '@userProfile/userProfile.models';
import { Note } from '@core/Note/Note';
import {
  useFetchDesignBriefsByT1,
  useFetchDesignerTransmittalsByT1,
  useFetchSiteTransmittalsByT1,
} from '@common/detailPageRightMenu/DetailPageRightMenu.queries';
import { EDesignBriefStatus } from '@designBrief/designBrief.constants';
import { ETransmittalStatus } from '@transmittal/transmittal.constants';
import { FormTextareaWithMaxLength } from '@core/FormTextareaWithMaxLength';
import { QueryObserver, useQueryClient } from '@tanstack/react-query';
import { IUseFetchOptionsResult } from '@common/SelectAsyncPaginate';
import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { EMasterScheduleFormKey } from './masterScheduleDetail.models';

interface IMasterScheduleDetailResponsiblePersonsAndTeamsProps {
  formFieldMethods: FormFieldMethods;
  checkerCompanyOptions: IOption<IProjectCompanyOption>[];
  designerCompanyOptions: IOption<IProjectCompanyOption>[];
  reviewCompanyOptions: IOption<IProjectCompanyOption>[];
  siteOperationTeamOptions: IOption<IProjectTeamOption>[];
  projectId: number;
  masterScheduleId?: number;
  isReadOnly: boolean;
  mode: DetailPageAccessMode;
}

export const MasterScheduleDetailResponsiblePersonsAndTeams: React.FunctionComponent<
  IMasterScheduleDetailResponsiblePersonsAndTeamsProps
> = ({
  formFieldMethods,
  siteOperationTeamOptions,
  checkerCompanyOptions,
  designerCompanyOptions,
  reviewCompanyOptions,
  projectId,
  masterScheduleId,
  isReadOnly,
  mode,
}) => {
  const queryClient = useQueryClient();

  const {
    watch,
    formState: { isDirty },
  } = formFieldMethods;

  const riskCategory: IRiskCategory = watch(EMasterScheduleFormKey.RISK_CATEGORY);
  const siteOperationTeam: IProjectTeamOption[] = watch(EMasterScheduleFormKey.RESPONSIBLE_SITE_OPERATION_TEAM);
  const checker: IProjectCompanyOption = watch(EMasterScheduleFormKey.RESPONSIBLE_CHECKER);
  const designer: IProjectCompanyOption = watch(EMasterScheduleFormKey.RESPONSIBLE_DESIGNER);
  const reviewer: IProjectCompanyOption = watch(EMasterScheduleFormKey.RESPONSIBLE_REVIEWER);
  const falsework = watch(EMasterScheduleFormKey.FALSEWORK);
  const hasNoDesigner = watch(EMasterScheduleFormKey.HAS_NO_DESIGNER);

  const fetchByT1Params = React.useMemo(() => {
    return { projectId, projectMasterScheduleId: masterScheduleId ?? 0 };
  }, [projectId, masterScheduleId]);

  const { data: designBriefsByT1 } = useFetchDesignBriefsByT1(fetchByT1Params, false);
  const { data: designerTransmittalsByT1 } = useFetchDesignerTransmittalsByT1(fetchByT1Params, false);
  const { data: siteTransmittalsByT1 } = useFetchSiteTransmittalsByT1(fetchByT1Params, false);

  const disableDesigner = React.useMemo(() => {
    const isAnyDtApproved = !!designerTransmittalsByT1?.find((dt) => dt.status === ETransmittalStatus.APPROVED);
    if (isAnyDtApproved) return true;

    const isAnyStApproved = !!siteTransmittalsByT1?.find((dt) => dt.status === ETransmittalStatus.APPROVED);
    if (isAnyStApproved) return true;

    return false;
  }, [JSON.stringify(designerTransmittalsByT1), JSON.stringify(siteTransmittalsByT1)]);

  const needT2ReviseIfDesignerChanged = React.useMemo(() => {
    return !!designBriefsByT1?.find(
      (t2) => t2.status === EDesignBriefStatus.DESIGNER_RECEIVED || t2.status === EDesignBriefStatus.FINISHED,
    );
  }, [JSON.stringify(designBriefsByT1)]);
  const [isShowNeedT2Revise, setIsShowNeedT2Revise] = React.useState(false);

  React.useEffect(() => {
    if (
      !formFieldMethods.formState.isDirty &&
      mode !== 'Create' &&
      !formFieldMethods.getFieldState(EMasterScheduleFormKey.RESPONSIBLE_SITE_OPERATION_TEAM).isDirty
    ) {
      return () => {};
    }
    formFieldMethods.setValue(EMasterScheduleFormKey.RESPONSIBLE_SRP, undefined);
    formFieldMethods.setValue(EMasterScheduleFormKey.RESPONSIBLE_TWC, undefined);
    formFieldMethods.setValue(EMasterScheduleFormKey.RESPONSIBLE_TWCM, undefined);

    if ((siteOperationTeam ?? 0).length <= 0) return () => {};

    const observerSrp = new QueryObserver(queryClient, {
      queryKey: [
        JSON.stringify(siteOperationTeamIds),
        projectId,
        FETCH_QUERIES_NAME.SRP_OPTIONS,
        undefined,
        0,
        DEFAULT_SELECT_PAGE_SIZE,
      ],
    });
    const observerTwc = new QueryObserver(queryClient, {
      queryKey: [
        JSON.stringify(siteOperationTeamIds),
        projectId,
        FETCH_QUERIES_NAME.TWC_OPTIONS,
        undefined,
        0,
        DEFAULT_SELECT_PAGE_SIZE,
      ],
    });

    const unsubscribeSrp = observerSrp.subscribe((result) => {
      const { options } = result.data as IUseFetchOptionsResult<IProjectUserProfileWithTeamOption>;
      if ((options?.length ?? 0) > 0) {
        formFieldMethods.setValue(EMasterScheduleFormKey.RESPONSIBLE_SRP, userOutputTransform(options));
      }
    });

    const unsubscribeTwc = observerTwc.subscribe((result) => {
      const { options } = result.data as IUseFetchOptionsResult<IProjectUserProfileWithTeamOption>;
      if ((options?.length ?? 0) > 0) {
        formFieldMethods.setValue(EMasterScheduleFormKey.RESPONSIBLE_TWC, userOutputTransform(options));
      }
    });
    return () => {
      unsubscribeSrp();
      unsubscribeTwc();
    };
  }, [JSON.stringify(siteOperationTeam)]);
  React.useEffect(() => {
    setIsShowNeedT2Revise(
      formFieldMethods.getFieldState(EMasterScheduleFormKey.RESPONSIBLE_DESIGNER).isDirty && !!designer,
    );

    formFieldMethods.setValue(EMasterScheduleFormKey.RESPONSIBLE_DESIGNER_TEAM, undefined);
  }, [JSON.stringify(designer)]);
  React.useEffect(() => {
    formFieldMethods.setValue(EMasterScheduleFormKey.RESPONSIBLE_CHECKER_TEAM, undefined);
  }, [JSON.stringify(checker)]);
  React.useEffect(() => {
    formFieldMethods.setValue(EMasterScheduleFormKey.RESPONSIBLE_REVIEWER_TEAM, undefined);
  }, [JSON.stringify(reviewer)]);

  const siteOperationTeamIds = React.useMemo(() => {
    return (siteOperationTeam ?? [])
      .map((item) => {
        return item.id;
      })
      .filter((i): i is number => !!i);
  }, [JSON.stringify(siteOperationTeam)]);

  React.useEffect(() => {
    if (hasNoDesigner === true) {
      formFieldMethods.setValue(EMasterScheduleFormKey.RESPONSIBLE_DESIGNER, null);
      formFieldMethods.setValue(EMasterScheduleFormKey.RESPONSIBLE_DESIGNER_TEAM, []);
    }
    if (hasNoDesigner === false) {
      formFieldMethods.setValue(EMasterScheduleFormKey.REMARK_FOR_NO_DESIGNER, undefined);
    }
  }, [JSON.stringify(hasNoDesigner)]);

  const { data: designerTeamOptions, isLoading: isLoadingDesignerTeamOptions } = useFetchTeamsOptionsByCompanyId({
    shouldConvertOptions: false,
  })({ projectId, companyId: designer?.id ?? 0 }, isPositiveNumber(designer?.id));

  const { data: checkerTeamOptions, isLoading: isLoadingCheckerTeamOptions } = useFetchTeamsOptionsByCompanyId({
    shouldConvertOptions: false,
  })({ projectId, companyId: checker?.id ?? 0 }, isPositiveNumber(checker?.id));

  const { data: reviewerTeamOptions, isLoading: isLoadingReviewerTeamOptions } = useFetchTeamsOptionsByCompanyId({
    shouldConvertOptions: false,
  })({ projectId, companyId: reviewer?.id ?? 0 }, isPositiveNumber(reviewer?.id));

  React.useEffect(() => {
    if (formFieldMethods.getValues(EMasterScheduleFormKey.RESPONSIBLE_DESIGNER_TEAM)) return;
    const defaultOptions = (designerTeamOptions ?? []).filter((o: any) => o.isDefault === true);
    formFieldMethods.setValue(EMasterScheduleFormKey.RESPONSIBLE_DESIGNER_TEAM, defaultOptions);
  }, [JSON.stringify(designerTeamOptions)]);
  React.useEffect(() => {
    if (formFieldMethods.getValues(EMasterScheduleFormKey.RESPONSIBLE_CHECKER_TEAM)) return;
    const defaultOptions = (checkerTeamOptions ?? []).filter((o: any) => o.isDefault === true);
    formFieldMethods.setValue(EMasterScheduleFormKey.RESPONSIBLE_CHECKER_TEAM, defaultOptions);
  }, [JSON.stringify(checkerTeamOptions)]);
  React.useEffect(() => {
    if (formFieldMethods.getValues(EMasterScheduleFormKey.RESPONSIBLE_REVIEWER_TEAM)) return;
    const defaultOptions = (reviewerTeamOptions ?? []).filter((o: any) => o.isDefault === true);
    formFieldMethods.setValue(EMasterScheduleFormKey.RESPONSIBLE_REVIEWER_TEAM, defaultOptions);
  }, [JSON.stringify(reviewerTeamOptions)]);

  const isReviewerRequired =
    (riskCategory?.category === ERiskCategory.A || riskCategory?.category === ERiskCategory.B) &&
    !!falsework &&
    checker?.organizationTypeCode !== EOrganizationTypeCode.LAL &&
    designer?.organizationTypeCode !== EOrganizationTypeCode.LAL;

  const onChangeConvert = (selectedList: IProjectUserProfileWithTeamOption[]) => {
    return selectedList.map((selected) => {
      return {
        id: selected.id,
        projectId,
        projectMasterScheduleId: masterScheduleId,
        projectUserProfileId: selected.id,
        displayName: selected.displayName,
      };
    });
  };

  const userInputTransform = (
    value: IOption<IProjectUserProfileWithTeamOption> | IOption<IProjectUserProfileWithTeamOption>[] | undefined | null,
  ) => {
    if (value === undefined || value === null) return undefined;
    return value;
  };
  const userOutputTransform = (value: IOption<IProjectUserProfileWithTeamOption>[] | undefined | null) => {
    if (value === undefined || value === null) return undefined;
    return (value as IOption<IProjectUserProfileWithTeamOption>[]).map((v) => {
      const temp = { ...v, ...v.data } as IProjectUserProfileWithTeamOption;
      return {
        id: temp.id,
        projectId,
        projectMasterScheduleId: masterScheduleId,
        projectUserProfileId: temp.id,
        displayName: temp.displayName,
      };
    });
  };

  const teamInputTransform = (
    value: IOption<IProjectTeamOption> | IOption<IProjectTeamOption>[] | undefined | null,
  ) => {
    if (value === undefined || value === null) return undefined;
    return value;
  };
  const teamOutputTransform = (value: IOption<IProjectTeamOption>[] | undefined | null) => {
    if (value === undefined || value === null) return undefined;
    return (value as IOption<IProjectTeamOption>[]).map((v) => {
      const temp = { ...v, ...v.data } as IProjectTeamOption;
      return {
        id: temp.id,
        projectId,
        projectMasterScheduleId: masterScheduleId,
        projectTeamId: temp.id,
        teamName: temp.teamName,
        rowVersion: undefined,
      };
    });
  };
  return (
    <>
      <Grid container spacing={1.5} columnSpacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <LabelWithMessage
            labelFor={EMasterScheduleFormKey.RESPONSIBLE_SITE_OPERATION_TEAM}
            text="Site Operation Team"
            required
          />
          <FormSelect
            options={siteOperationTeamOptions}
            formFieldMethods={formFieldMethods}
            displayFieldName="teamName"
            id={EMasterScheduleFormKey.RESPONSIBLE_SITE_OPERATION_TEAM}
            placeholder="Select Site Operation Team ..."
            rules={requiredRule}
            multiple
            transform={{
              input: teamInputTransform,
              output: teamOutputTransform,
            }}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <LabelWithMessage labelFor={EMasterScheduleFormKey.RESPONSIBLE_SRP} text="SRP/QP" required />
          <FormSelectAsyncPaginate
            groupOptionByKey="team.teamName"
            shouldDebounce
            multiple
            pageSize={DEFAULT_SELECT_PAGE_SIZE}
            rules={{
              ...requiredRule,
              validate: (value: IProjectUserProfileWithTeamOption[]) => {
                if ((value ?? []).some((s) => s.displayName === SYSTEM_ACCOUNT_DISPLAY_NAME))
                  return 'Inappropriate SRP. Please review.';
                return true;
              },
            }}
            placeholder="Select SRP..."
            displayFieldName="displayName"
            formFieldMethods={formFieldMethods}
            useFetchOptions={useFetchSrpOptionsWithSearch({ shouldConvertOptions: false })}
            useFetchOptionsProps={{ projectId, projectTeamIds: siteOperationTeamIds }}
            id={EMasterScheduleFormKey.RESPONSIBLE_SRP}
            transform={{
              output: userOutputTransform,
              input: (inputValue) => {
                if (inputValue === undefined || inputValue === null) return undefined;
                return inputValue;
              },
            }}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <LabelWithMessage labelFor={EMasterScheduleFormKey.RESPONSIBLE_TWC} text="TWC/GE" />
          <FormSelectAsyncPaginate
            groupOptionByKey="team.teamName"
            shouldDebounce
            multiple
            pageSize={DEFAULT_SELECT_PAGE_SIZE}
            // rules={requiredRule}
            placeholder="Select TWC..."
            displayFieldName="displayName"
            formFieldMethods={formFieldMethods}
            useFetchOptions={useFetchTwcOptionsWithSearch({ shouldConvertOptions: false })}
            useFetchOptionsProps={{ projectId, projectTeamIds: siteOperationTeamIds }}
            id={EMasterScheduleFormKey.RESPONSIBLE_TWC}
            transform={{
              output: userOutputTransform,
              input: (inputValue) => {
                if (inputValue === undefined || inputValue === null) return undefined;
                return inputValue;
              },
            }}
            disabled={isReadOnly}
            rules={{
              validate: (value: IProjectUserProfileWithTeamOption[]) => {
                if ((value ?? []).some((s) => s.displayName === SYSTEM_ACCOUNT_DISPLAY_NAME))
                  return 'Inappropriate TWC. Please review.';
                return true;
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <LabelWithMessage labelFor={EMasterScheduleFormKey.RESPONSIBLE_TWCM} text="TWCM" />
          <FormSelectAsyncPaginate
            groupOptionByKey="team.teamName"
            shouldDebounce
            multiple
            pageSize={DEFAULT_SELECT_PAGE_SIZE}
            placeholder="Select TWCM..."
            displayFieldName="displayName"
            formFieldMethods={formFieldMethods}
            useFetchOptions={useFetchTwcmOptionsWithSearch({ shouldConvertOptions: false })}
            useFetchOptionsProps={{ projectId, projectTeamIds: siteOperationTeamIds }}
            id={EMasterScheduleFormKey.RESPONSIBLE_TWCM}
            transform={{
              output: userOutputTransform,
              input: (inputValue) => {
                if (inputValue === undefined || inputValue === null) return undefined;
                return inputValue;
              },
            }}
            disabled={isReadOnly}
            rules={{
              validate: (value: IProjectUserProfileWithTeamOption[]) => {
                if ((value ?? []).some((s) => s.displayName === SYSTEM_ACCOUNT_DISPLAY_NAME))
                  return 'Inappropriate TWCM. Please review.';
                return true;
              },
            }}
          />
        </Grid>
        <Grid container item xs={12} spacing={1.5} columnSpacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <LabelWithMessage labelFor={EMasterScheduleFormKey.HAS_NO_DESIGNER} text="Has no GEMS designer" />
            <FormCheckbox
              formFieldMethods={formFieldMethods}
              id={EMasterScheduleFormKey.HAS_NO_DESIGNER}
              disabled={isReadOnly || disableDesigner}
            />
          </Grid>
          {hasNoDesigner && (
            <>
              <Grid item xs={12} sm={9}>
                <LabelWithMessage
                  labelFor={EMasterScheduleFormKey.REMARK_FOR_NO_DESIGNER}
                  required
                  text="Remark for no designer"
                />
                <FormTextareaWithMaxLength
                  max={3000}
                  id={EMasterScheduleFormKey.REMARK_FOR_NO_DESIGNER}
                  formFieldMethods={formFieldMethods}
                  disabled={isReadOnly || disableDesigner}
                  rules={{ ...(hasNoDesigner && requiredRule) }}
                />
              </Grid>
            </>
          )}
          {!hasNoDesigner && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <LabelWithMessage
                  labelFor={EMasterScheduleFormKey.RESPONSIBLE_DESIGNER}
                  text="Designer"
                  required
                  testId="t1-form-designer-company"
                />
                <FormSelect
                  options={designerCompanyOptions}
                  formFieldMethods={formFieldMethods}
                  displayFieldName="name"
                  id={EMasterScheduleFormKey.RESPONSIBLE_DESIGNER}
                  placeholder="Select Designer..."
                  rules={{
                    ...(!hasNoDesigner && requiredRule),
                    validate: (value: IProjectCompanyOption) => {
                      if (value?.maxRiskCategory > riskCategory?.category)
                        return 'Inappropriate designer for this risk category. Please review.';
                      return true;
                    },
                  }}
                  disabled={isReadOnly || disableDesigner}
                />
                {isShowNeedT2Revise && needT2ReviseIfDesignerChanged && (
                  <Note
                    className="gcl-fb-md gcl-mt-sm"
                    componentType={ComponentType.Danger}
                    prefixIcon={<Icon iconName={'ExclamationCircle' as IconName} />}
                  >
                    Please revise the T2 and reissue to the correct TWD.
                  </Note>
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <LabelWithMessage
                  labelFor={EMasterScheduleFormKey.RESPONSIBLE_DESIGNER_TEAM}
                  text="Designer Team"
                  required
                />
                <FormSelect
                  isLoading={isLoadingDesignerTeamOptions && !!designer}
                  options={designerTeamOptions || []}
                  formFieldMethods={formFieldMethods}
                  displayFieldName="teamName"
                  id={EMasterScheduleFormKey.RESPONSIBLE_DESIGNER_TEAM}
                  placeholder="Select Designer Team..."
                  disabled={!designer || isReadOnly || disableDesigner}
                  rules={{ ...(!hasNoDesigner && requiredRule) }}
                  multiple
                  transform={{
                    input: teamInputTransform,
                    output: teamOutputTransform,
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <LabelWithMessage
            labelFor={EMasterScheduleFormKey.RESPONSIBLE_CHECKER}
            text="Checker"
            required
            testId="t1-form-checker-company"
          />
          <FormSelect
            options={checkerCompanyOptions}
            formFieldMethods={formFieldMethods}
            displayFieldName="name"
            id={EMasterScheduleFormKey.RESPONSIBLE_CHECKER}
            placeholder="Select Checker..."
            rules={{
              ...requiredRule,
              validate: (value: IProjectCompanyOption) => {
                if (value.maxRiskCategory > riskCategory?.category)
                  return 'Inappropriate checker for this risk category. Please review.';
                return true;
              },
            }}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <LabelWithMessage labelFor={EMasterScheduleFormKey.RESPONSIBLE_CHECKER_TEAM} text="Checker Team" required />
          <FormSelect
            isLoading={isLoadingCheckerTeamOptions && !!checker}
            options={checkerTeamOptions || []}
            formFieldMethods={formFieldMethods}
            displayFieldName="teamName"
            id={EMasterScheduleFormKey.RESPONSIBLE_CHECKER_TEAM}
            placeholder="Select Checker Team..."
            disabled={!checker || isReadOnly}
            rules={requiredRule}
            multiple
            transform={{
              input: teamInputTransform,
              output: teamOutputTransform,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <LabelWithMessage
            testId="t1-form-reviewer-company"
            labelFor={EMasterScheduleFormKey.RESPONSIBLE_REVIEWER}
            text="Reviewer"
            required={isReviewerRequired}
          />
          <FormSelect
            options={reviewCompanyOptions}
            formFieldMethods={formFieldMethods}
            displayFieldName="name"
            id={EMasterScheduleFormKey.RESPONSIBLE_REVIEWER}
            placeholder="Select Reviewer..."
            rules={{
              validate: (value: IProjectCompanyOption) => {
                if (isReviewerRequired && value?.organizationTypeCode !== EOrganizationTypeCode.LAL)
                  return 'For falsework with category A or B, Lambeth has to be either designer / checker / reviewer.';
                return true;
              },
            }}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <LabelWithMessage
            labelFor={EMasterScheduleFormKey.RESPONSIBLE_REVIEWER_TEAM}
            text="Reviewer Team"
            required={isReviewerRequired}
          />
          <FormSelect
            isLoading={isLoadingReviewerTeamOptions && !!reviewer}
            options={reviewerTeamOptions || []}
            formFieldMethods={formFieldMethods}
            displayFieldName="teamName"
            id={EMasterScheduleFormKey.RESPONSIBLE_REVIEWER_TEAM}
            placeholder="Select Reviewer Team..."
            disabled={!reviewer || isReadOnly}
            multiple
            rules={{
              validate: (value: IProjectTeamOption[]) => {
                if (isReviewerRequired && (!value || value.length <= 0)) return requiredRule.required.message;
                return true;
              },
            }}
            transform={{
              input: teamInputTransform,
              output: teamOutputTransform,
            }}
          />
        </Grid>
        {formFieldMethods.getValues(EMasterScheduleFormKey.CREATED_BY) && (
          <Grid item xs={12} sm={6} md={3}>
            <LabelWithMessage labelFor="initiatedBy" text="Initiated By" />
            <FormText id={EMasterScheduleFormKey.CREATED_BY} formFieldMethods={formFieldMethods} />
          </Grid>
        )}
      </Grid>
    </>
  );
};
